/*
 * "Colar de Letra de Brinde" (BOGOS) free-gift picker — INDEPENDENT of
 * bogos-cart-sync.js (porta-joias). Do not merge these two scripts.
 * ------------------------------------------------------------------
 * WHY THIS EXISTS: same root cause as the porta-joias bridge — BOGOS's gift
 * engine only activates on cart UIs it recognizes (UpCart, CartPopup,
 * AfterSell, etc.) and stays completely inert on this theme's native cart.
 *
 * WHY IT'S A SEPARATE FILE FROM THE PORTA-JOIAS SCRIPT: that offer was a
 * single-variant silent auto-add. This offer's gift is a personalized
 * necklace (one letter A-Z) — a silent auto-add would hand the customer an
 * arbitrary letter, so it needs an actual picker UI, not an auto-add.
 *
 * 2026-08-10 update: the merchant added more style options back to this
 * offer in the BOGOS app (it had been narrowed to a single style earlier
 * the same day). BOGOS clones one gift product PER STYLE — ported the
 * multi-style picker logic from the "Murano [COLAR DE LETRA]" theme's
 * assets/bogos-gift-picker.js (same custom-element pattern already used
 * here) instead of hardcoding a single product/handle again, since that's
 * exactly the assumption that broke last time the offer was edited. Every
 * gift candidate is fetched independently and silently dropped on 404.
 *
 * 2026-09-25 update: the offer is now value-based — cart >= R$600 in any
 * product (BOGOS offer 207184, condition unit "money"), no longer 6+ pieces
 * — with styles bold / minimalista / zirconia. The rule and the gift
 * handles are now read from BOGOS's own offer data (window.fgData.offers,
 * see liveOffer()). Contrary to what was assumed in 2026-08, that data does
 * reach this page: BOGOS's data bundle just loads after this script, so it
 * only exists once the page has loaded (see `ready` below). FALLBACK_RULE
 * and GIFT_HANDLE_CANDIDATES only apply when it isn't on the page (e.g. the
 * bundle failed to load).
 *
 * SAFETY: never auto-adds the gift — only removes it, when the cart drops
 * below the threshold. Adding always requires the customer to pick a style
 * (if more than one is live) and a letter.
 */
(function () {
  if (window.__bogosLetterGiftInstalled) return;
  window.__bogosLetterGiftInstalled = true;

  var CLONE_SUFFIX = "sca_clone_freegift";
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  var MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i;
  var DEBOUNCE_MS = 550;

  // Fallback qualifying rule, in the shape of BOGOS's own offer_conditions
  // (min_amount + unit). Mirrors offer 207184 as decoded 2026-09-25: cart
  // value >= R$600, any product. currentRule() prefers the live offer.
  var FALLBACK_RULE = { unit: "money", minAmount: 600 };

  // Fallback gift products, one BOGOS clone per style (26 variants each, one
  // per letter, all R$0). Confirmed live 2026-09-25: these three resolve;
  // marble and the older styles now 404. resolveGiftOptions() prefers the
  // live offer's list.
  var GIFT_HANDLE_CANDIDATES = [
    "colar-letra-inicial-bold-banhado-em-ouro-18k-sca_clone_freegift",
    "colar-letra-inicial-minimalista-banhado-em-ouro-18k-sca_clone_freegift",
    "colar-letra-inicial-zirconia-banhado-em-ouro-18k-sca_clone_freegift",
  ];

  var instances = [];
  var timer = null;
  var reconciling = false;
  var selfMutating = false;
  var giftOptions = null; // resolved lazily: [{handle, title, image, variants:[{id,label}]}]
  var giftOptionsPromise = null;
  var discoveryAttempts = 0;
  var MAX_DISCOVERY_ATTEMPTS = 5;

  function pathnameOf(url) {
    try { return new URL(url, window.location.origin).pathname; }
    catch (e) { return String(url || "").split("?")[0].split("#")[0]; }
  }
  function isCartMutation(url) {
    if (!url) return false;
    return MUTATION_RE.test(pathnameOf(url)) || MUTATION_RE.test(String(url));
  }

  // This script only owns the colar-de-letra gift lines. The
  // "sca_clone_freegift" suffix is shared by EVERY gift BOGOS clones —
  // including the porta-joias handled by assets/bogos-cart-sync.js. Matching
  // on the suffix alone made this script treat the porta-joias as its own
  // gift: with 3 items (below this offer's 6) it removed it, bogos-cart-sync
  // re-added it, and the two looped forever (found live 2026-09-18). Same
  // scoping bogos-cart-sync.js already applies in the other direction.
  var LETTER_HANDLE_RE = /colar-letra/i;
  function isLetterGiftHandle(handle) {
    return typeof handle === "string" &&
      handle.indexOf(CLONE_SUFFIX) !== -1 &&
      LETTER_HANDLE_RE.test(handle);
  }

  // The offer as BOGOS itself is running it, from window.fgData.offers —
  // filled by BOGOS's data bundle (freegifts_data_*.min.js), which loads
  // after this script, so this is null until then or if the bundle never
  // loads. Picked by its gift products, scoped to colar-de-letra handles for
  // the reason above — otherwise another offer would be read as this one.
  function liveOffer() {
    try {
      var offers = (window.fgData && window.fgData.offers) || [];
      for (var i = 0; i < offers.length; i++) {
        var handles = (offers[i].gift_products || [])
          .map(function (g) { return g && g.clone_product_handle; })
          .filter(isLetterGiftHandle);
        if (handles.length) return { offer: offers[i], handles: handles };
      }
    } catch (e) {}
    return null;
  }

  // Only BOGOS's min_amount + unit are replicated — any product/collection
  // narrowing on the offer is not (none is configured as of 2026-09-25).
  function currentRule() {
    var live = liveOffer();
    var cond = live && live.offer.offer_conditions && live.offer.offer_conditions[0];
    var amount = cond ? parseFloat(cond.min_amount) : NaN;
    if (amount > 0 && cond.unit === "money") return { unit: "money", minAmount: amount };
    if (amount > 0 && cond.unit === "number_of_products") return { unit: "quantity", minAmount: amount };
    return FALLBACK_RULE;
  }

  function cleanTitle(title) {
    return String(title || "")
      .replace(/[​-\u200F﻿]/g, "")
      .replace(/\(?100%\s*off\)?/gi, "")
      .trim();
  }

  // Builds a variant's display label from whichever option actually varies
  // (usually just "Letra") — survives BOGOS reordering/renaming options.
  function variantLabel(product, variant) {
    var opts = product.options || [];
    var parts = [];
    for (var i = 0; i < opts.length; i++) {
      if (opts[i] && opts[i].values && opts[i].values.length > 1) {
        var val = variant["option" + (i + 1)];
        if (val) parts.push(val);
      }
    }
    return parts.length ? parts.join(" / ") : variant.title;
  }

  function fetchProduct(handle) {
    return fetch(ROOT + "products/" + handle + ".js", { headers: { Accept: "application/json" }, cache: "no-store", credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { return p && p.variants && p.variants.length ? p : null; })
      .catch(function () { return null; });
  }

  function resolveGiftOptions(forceRetry) {
    if (giftOptions && !forceRetry) return Promise.resolve(giftOptions);
    if (giftOptionsPromise && !forceRetry) return giftOptionsPromise;

    var live = liveOffer();
    var handles = live ? live.handles : GIFT_HANDLE_CANDIDATES;
    var seen = {};
    handles = handles.filter(function (h) { return h && !seen[h] && (seen[h] = 1); });
    if (!handles.length) {
      giftOptions = [];
      return Promise.resolve(giftOptions);
    }

    giftOptionsPromise = Promise.all(handles.map(fetchProduct)).then(function (products) {
      giftOptions = products.filter(Boolean).map(function (p) {
        return {
          handle: p.handle,
          title: cleanTitle(p.title),
          image: p.featured_image || (p.images && p.images[0]) || null,
          variants: p.variants
            .map(function (v) { return { id: v.id, label: variantLabel(p, v) }; })
            .sort(function (a, b) { return a.label.localeCompare(b.label); }),
        };
      });
      giftOptionsPromise = null;
      return giftOptions;
    });
    return giftOptionsPromise;
  }

  function isGiftItem(item) {
    return isLetterGiftHandle(item.handle);
  }

  function getCart() {
    return fetch(ROOT + "cart.js", { headers: { Accept: "application/json" }, cache: "no-store", credentials: "same-origin" })
      .then(function (r) { return r.json(); });
  }

  function changeLineQuantity(lineKey, quantity) {
    selfMutating = true;
    return fetch(ROOT + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    }).finally(function () { selfMutating = false; });
  }

  function removeLine(lineKey) {
    return changeLineQuantity(lineKey, 0);
  }

  function addVariant(variantId) {
    selfMutating = true;
    return fetch(ROOT + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    }).finally(function () { selfMutating = false; });
  }

  // Splits the cart into this offer's gift lines and everything else, and
  // checks everything else against the rule. Money sums final_line_price —
  // the field BOGOS itself sums (its sca_cart_discount_price default), and on
  // this store it ignores discount codes, so a coupon can't un-earn the gift.
  function summarize(cart) {
    var giftLines = [];
    var qty = 0;
    var cents = 0;
    (cart.items || []).forEach(function (it) {
      if (isGiftItem(it)) { giftLines.push(it); return; }
      qty += it.quantity;
      cents += it.final_line_price || 0;
    });
    var rule = currentRule();
    return {
      giftLines: giftLines,
      qualifies: rule.unit === "money" ? cents >= Math.round(rule.minAmount * 100) : qty >= rule.minAmount,
    };
  }

  function buildState(cart, options) {
    var summary = summarize(cart);
    return {
      qualifies: summary.qualifies,
      giftLine: summary.giftLines[0] || null,
      options: options || [],
      loading: options === null,
    };
  }

  // Cached across every instance so a freshly (re)created element (see
  // connectedCallback below) can repaint synchronously instead of flashing
  // back to its default hidden state while a fresh reconcile() round-trip
  // is in flight.
  var lastState = null;

  function renderAll(state) {
    lastState = state;
    instances.forEach(function (el) { el.render(state); });
  }

  function reconcile() {
    if (reconciling) return Promise.resolve(false);
    reconciling = true;

    return getCart()
      .then(function (cart) {
        var summary = summarize(cart);
        var giftLines = summary.giftLines;

        // Rule: only one gift unit ever, regardless of which style/letter was
        // picked. Fixes both ways a second one can land — a race between two
        // rendered widget instances (drawer + full /cart page) adding two
        // different variants as separate lines, and the cart's own quantity
        // stepper bumping the single gift line above 1 — by correcting the
        // cart on the very next mutation, same self-healing pattern as the
        // below-threshold removal already does.
        if (giftLines.length > 1) {
          var keep = giftLines[0];
          var extras = giftLines.slice(1);
          return Promise.all(extras.map(function (it) { return removeLine(it.key); }))
            .then(function () { return keep.quantity > 1 ? changeLineQuantity(keep.key, 1) : null; })
            .then(function () { return true; });
        }
        if (giftLines.length === 1 && giftLines[0].quantity > 1) {
          return changeLineQuantity(giftLines[0].key, 1).then(function () { return true; });
        }

        var giftLine = giftLines[0] || null;
        var qualifies = summary.qualifies;

        // Below threshold with a gift already in the cart: remove it. Adding
        // is always the customer's choice via the picker — never automatic.
        if (!qualifies && giftLine) {
          return removeLine(giftLine.key).then(function () { return true; });
        }

        renderAll(buildState(cart, giftOptions));

        if (qualifies && !giftLine && giftOptions === null) {
          resolveGiftOptions().then(function (opts) {
            renderAll(buildState({ items: cart.items }, opts));
            if (!opts.length && discoveryAttempts < MAX_DISCOVERY_ATTEMPTS) {
              discoveryAttempts++;
              setTimeout(function () { resolveGiftOptions(true).then(function (o) { renderAll(buildState({ items: cart.items }, o)); }); }, 1200 * discoveryAttempts);
            }
          });
        }
        return false;
      })
      .catch(function () { return false; })
      .finally(function () { reconciling = false; });
  }

  // BOGOS's data bundle is a deferred script placed after this one, so it
  // hasn't run yet when this script does; every deferred script has run by
  // DOMContentLoaded. Holding syncs until then makes even the first
  // reconcile use the live rule, rather than possibly removing a gift under
  // a stale FALLBACK_RULE and re-offering it a moment later.
  var ready = document.readyState === "complete";
  if (!ready) {
    var onReady = function () {
      if (ready) return;
      ready = true;
      runSync();
    };
    document.addEventListener("DOMContentLoaded", onReady);
    window.addEventListener("load", onReady);
  }

  function runSync() {
    if (!ready) return;
    reconcile().then(function (changed) {
      if (changed) document.dispatchEvent(new CustomEvent("cart:refresh"));
    });
  }

  function schedule() {
    if (selfMutating) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () { timer = null; runSync(); }, DEBOUNCE_MS);
  }

  /* ---- Patch fetch/XHR so we notice every cart mutation, from any script.
     Guarded separately from bogos-cart-sync.js's own patch flag, since each
     wraps whatever window.fetch/XMLHttpRequest.prototype.send currently is
     and calls through it — chaining is safe. ---- */
  if (!window.__bogosLetterGiftPatched) {
    window.__bogosLetterGiftPatched = true;
    if (typeof window.fetch === "function") {
      var originalFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === "string" ? input : (input && input.url);
        var mutation = isCartMutation(url);
        var result = originalFetch.apply(this, arguments);
        if (mutation && result && typeof result.then === "function") result.then(schedule, schedule);
        return result;
      };
    }
    if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
      var proto = window.XMLHttpRequest.prototype;
      var originalOpen = proto.open;
      proto.open = function (method, url) {
        this.__bogosLetterGiftIsCartMutation = isCartMutation(url);
        return originalOpen.apply(this, arguments);
      };
      var originalSend = proto.send;
      proto.send = function () {
        if (this.__bogosLetterGiftIsCartMutation) this.addEventListener("loadend", schedule);
        return originalSend.apply(this, arguments);
      };
    }
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  class BogosLetterNecklaceGift extends HTMLElement {
    connectedCallback() {
      this._selectedHandle = null;
      instances.push(this);
      this.addEventListener("click", this._onClick.bind(this));
      // Repaint immediately from the last known cart state (see `lastState`)
      // before kicking off a fresh reconcile(). Needed because a sibling
      // script (bogos-cart-sync.js) dispatches "cart:refresh" on every cart
      // mutation, which makes the theme's own cart.js rewrite this element's
      // parent innerHTML — destroying and recreating this element from its
      // hidden-by-default liquid markup. Without this, the widget would
      // flash hidden on every such refresh while the async cart.js fetch
      // that follows is in flight, which reads as "appears then vanishes".
      if (lastState) this.render(lastState);
      runSync();
    }

    disconnectedCallback() {
      var idx = instances.indexOf(this);
      if (idx !== -1) instances.splice(idx, 1);
    }

    _onClick(e) {
      var styleBtn = e.target.closest && e.target.closest("[data-gift-style]");
      if (styleBtn) {
        this._selectedHandle = styleBtn.getAttribute("data-gift-style");
        this._renderCurrent();
        return;
      }
      var backBtn = e.target.closest && e.target.closest("[data-gift-back]");
      if (backBtn) {
        this._selectedHandle = null;
        this._renderCurrent();
        return;
      }
      var addBtn = e.target.closest && e.target.closest("[data-gift-add]");
      if (addBtn) this._handleAdd(addBtn);
    }

    _handleAdd(addBtn) {
      // Belt-and-suspenders against the drawer and full /cart page both
      // having a live, not-yet-hidden widget instance at once: if the last
      // known cart state already has a gift, don't even fire the add — let
      // reconcile()'s clamp (see above) be the only path that can ever leave
      // more than one gift line, never a second races-in add from here.
      if (lastState && lastState.giftLine) return;
      var select = this.querySelector("[data-gift-letter]");
      var variantId = select ? select.value : addBtn.getAttribute("data-gift-add");
      if (!variantId) {
        if (select) select.focus();
        return;
      }
      addBtn.disabled = true;
      addVariant(parseInt(variantId, 10))
        .then(function () {
          this._selectedHandle = null;
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        }.bind(this))
        ["catch"](function () { addBtn.disabled = false; });
    }

    _renderCurrent() {
      getCart().then(function (cart) {
        renderAll(buildState(cart, giftOptions));
      });
    }

    render(state) {
      if (!state.qualifies || state.giftLine) {
        this.setAttribute("hidden", "");
        this.innerHTML = "";
        return;
      }
      if (state.loading || !state.options) {
        this.removeAttribute("hidden");
        this.innerHTML = '<p class="m-bogos-letter-gift__loading">Carregando seu brinde…</p>';
        return;
      }
      if (!state.options.length) {
        this.setAttribute("hidden", "");
        this.innerHTML = "";
        return;
      }

      // Every path from here on shows the widget. Must remove "hidden"
      // explicitly — it's only ever added above, and buildState() skips the
      // "loading" branch (the one other place that removes it) once
      // giftOptions is already cached, which is exactly the case the second
      // time the widget needs to reappear (e.g. qty drops below threshold,
      // gift removed, qty goes back up). Without this, the element re-fills
      // its innerHTML with the picker but stays invisible.
      this.removeAttribute("hidden");

      // Only one live style: skip the style-selection step entirely, same
      // simplification as when this offer briefly had a single gift.
      if (state.options.length === 1) this._selectedHandle = state.options[0].handle;

      var selected = null;
      for (var i = 0; i < state.options.length; i++) {
        if (state.options[i].handle === this._selectedHandle) selected = state.options[i];
      }

      if (!selected) {
        var cards = state.options.map(function (opt) {
          var img = opt.image ? '<img src="' + escapeHtml(opt.image) + '&width=160" alt="" loading="lazy" width="56" height="56">' : "";
          return (
            '<button type="button" class="m-bogos-letter-gift__style" data-gift-style="' + escapeHtml(opt.handle) + '">' +
              '<span class="m-bogos-letter-gift__style-img">' + img + "</span>" +
              '<span class="m-bogos-letter-gift__style-label">' + escapeHtml(opt.title) + "</span>" +
              '<span class="m-bogos-letter-gift__style-cta">Escolher</span>' +
            "</button>"
          );
        }).join("");
        this.innerHTML =
          '<p class="m-bogos-letter-gift__title">Você ganhou um Colar de Letra de Brinde, escolha o seu modelo:</p>' +
          '<div class="m-bogos-letter-gift__grid">' + cards + "</div>";
        return;
      }

      var multiStyle = state.options.length > 1;
      var options = selected.variants.map(function (v) {
        return '<option value="' + v.id + '">' + escapeHtml(v.label) + "</option>";
      }).join("");

      this.innerHTML =
        '<p class="m-bogos-letter-gift__title">' + (multiStyle ? escapeHtml(selected.title) : "Você ganhou um Colar de Letra de Brinde, escolha o seu modelo:") + "</p>" +
        '<div class="m-bogos-letter-gift__row">' +
          '<select class="m-bogos-letter-gift__select" data-gift-letter aria-label="Escolha a letra">' +
            '<option value="" disabled selected>Escolha a letra</option>' + options +
          "</select>" +
          '<button type="button" class="m-bogos-letter-gift__add" data-gift-add>Adicionar</button>' +
        "</div>" +
        (multiStyle ? '<button type="button" class="m-bogos-letter-gift__back" data-gift-back>← Escolher outro estilo</button>' : "");
    }
  }

  if (!customElements.get("bogos-letter-necklace-gift")) {
    customElements.define("bogos-letter-necklace-gift", BogosLetterNecklaceGift);
  }
})();
